// Sign-in. In dev the API trusts X-User-Id (a UUID) + X-Role, so this screen
// collects those and persists them. In prod it's a single Sign in button that
// redirects to the Cognito Hosted UI (Authorization Code + PKCE); the token is
// captured on the /auth/callback route.
//
// This is the first screen every audience sees — a practice owner, a
// reviewing attorney, an internal admin — so it wears the customer design
// system (doc 26): the editorial wordmark, ink for the one action, nothing
// coloured. It says nothing about which surface will open, because that is
// decided from the grants after sign-in (CustomerEntry), not from the door.

import { useState, lazy, Suspense } from 'react';
import { AUTH_MODE } from '../auth/identity';
import { getCognitoConfig, isCognitoConfigured, loginWithProvider } from '../auth/cognito';
import { useAuth } from '../auth/AuthContext';
import { useSurfaceTheme } from '../customer/useSurfaceTheme';
import { useDeviceViewport } from '../portal/useDeviceViewport';
import { Button, Callout, Field, Select, TextInput } from '../portal/components/ui';
import { PASSKEY_COPY } from '../auth/strings/passkeys';
import { Link } from 'react-router-dom';
import { SIGNED_OUT_WORDS } from '../auth/support';

// A stable dev UUID so a reviewer doesn't have to invent one every time.
const SAMPLE_UUID = '00000000-0000-4000-8000-000000000001';

// The sign-in lattice (WOW3D) is its own chunk: the bundle every audience
// downloads first carries none of it, and the form paints before it loads.
const EntryBackdrop = lazy(() => import('../ui/three/EntryBackdrop').then((m) => ({ default: m.EntryBackdrop })));

export function Login() {
  useSurfaceTheme();
  useDeviceViewport();
  return (
    <div className="vx">
      <main className="vx-gate vx-gate--auth vx-stage">
        <Suspense fallback={null}>
          <EntryBackdrop />
        </Suspense>
        {/* A CSS rise, not the motion library: this screen is in the shared
            bundle every audience downloads, and the library belongs to the
            customer chunk only. */}
        <div className="vx-gate__card vx-rise">
          <span className="vx-wordmark">VerityRx</span>
          {AUTH_MODE === 'prod' ? <ProdLogin /> : <DevLogin />}
        </div>
      </main>
    </div>
  );
}

function ProdLogin() {
  const { login } = useAuth();
  const configured = isCognitoConfigured();

  return (
    <>
      <p className="vx-eyebrow" style={{ marginTop: 'var(--vx-8)' }}>
        For practices and reviewers
      </p>
      <h1>Sign in</h1>
      <p className="vx-lead" style={{ marginTop: 'var(--vx-4)' }}>
        Access is by invitation. Use the email address your invitation was sent to.
      </p>
      <div style={{ marginTop: 'var(--vx-8)' }}>
        {configured ? (
          <Button variant="primary" style={{ width: '100%' }} onClick={() => login()}>
            Continue to sign in
          </Button>
        ) : (
          <Callout strong>
            Sign-in is unavailable right now. Contact your VerityRx team.
          </Callout>
        )}
      </div>
      {configured && getCognitoConfig().firmProviders.map((provider) => (
        <div key={provider.id} style={{ marginTop: 'var(--vx-3)' }}>
          <Button
            variant="secondary"
            style={{ width: '100%' }}
            onClick={() => void loginWithProvider(provider.id, window.location.pathname + window.location.search)}
          >
            {PASSKEY_COPY.firmSignIn(provider.label)}
          </Button>
        </div>
      ))}
      <p className="vx-small vx-muted" style={{ marginTop: 'var(--vx-6)' }}>
        Have your authenticator app ready for a sign-in code.
      </p>
      {/* PM-40: a person without a working invitation had nowhere to go. */}
      <p className="vx-small vx-muted" style={{ marginTop: 'var(--vx-3)' }}>
        <Link to="/welcome">{SIGNED_OUT_WORDS.firstTime}</Link>
      </p>
      <p className="vx-small vx-muted" style={{ marginTop: 'var(--vx-3)' }} data-testid="login-lost-invitation">
        {SIGNED_OUT_WORDS.lostInvitation()}
      </p>
    </>
  );
}

function DevLogin() {
  const { login } = useAuth();
  const [userId, setUserId] = useState(SAMPLE_UUID);
  const [role, setRole] = useState('attorney');
  const [err, setErr] = useState('');

  const isUuid = (s: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());

  const submit = () => {
    if (!isUuid(userId)) {
      setErr('User ID must be a UUID (dev mode sends it as X-User-Id).');
      return;
    }
    login({ userId: userId.trim(), role });
  };

  return (
    <>
      <p className="vx-eyebrow" style={{ marginTop: 'var(--vx-8)' }}>
        Development sign-in
      </p>
      <h1>Enter as…</h1>
      <p className="vx-small vx-muted" style={{ marginTop: 'var(--vx-4)' }}>
        The API trusts these headers in dev environments only.
      </p>
      <div className="vx-stack" style={{ marginTop: 'var(--vx-7)' }}>
        <Field label="User ID (UUID)">
          <TextInput
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder={SAMPLE_UUID}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
          />
        </Field>
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="attorney">attorney</option>
            <option value="internal_admin">VerityRx team</option>
            <option value="customer">customer (portal)</option>
          </Select>
        </Field>
        {err && <Callout strong>{err}</Callout>}
        <Button variant="primary" style={{ width: '100%' }} onClick={submit}>
          Enter
        </Button>
      </div>
    </>
  );
}
